import { useContext, useEffect } from "react";
// my imports:
import { handleKeyDown } from "@/app/lib/input/handle-key-down";
import { handleLetterInput } from "@/app/lib/input/handle-letter-input";
import { AnyGameObj } from "@/app/lib/game-state/game-types";
import UI from "@/app/lib/ui/ui";
import { Contexts } from "@/app/components/Context";
import { StoryGameState } from "@/app/lib/game-state/story-game-state";
// styles:
import styles from '@/app/styles/letterBox.module.css';
import animation from "@/app/styles/animation.module.css";
import { useAnimationTrigger } from "@/app/lib/ui/use-animation-trigger";

export type LetterBoxProps = {
    wordIndex: number,
    letterPosition: number,
}

export default function LetterBox({ wordIndex, letterPosition }: LetterBoxProps) {

    const activeGame: AnyGameObj | null = useContext(Contexts.ActiveGame);    
    const ui: UI | null = useContext(Contexts.UI);

    if (!activeGame || !ui ) {
        console.warn('bad context in LetterBox')
        return;
    }

    // we access the modules directly from the parent state, essential for rendering correctly
    const letterModule = activeGame.state.words[wordIndex].letters[letterPosition];
    const wordModule = activeGame.state.words[wordIndex];

    let classString: string = `${styles.letterBox}`;
    let tabIndex: number = 0;
    if (wordModule.solved) {
        classString += ` ${styles.solved}`;
    }
    // we only do inactive if it's not solved, solved takes precedence
    else if (!wordModule.active) {
        classString += ` ${styles.inactive}`;
        // tabIndex = -1;
        if (letterModule.type === 'entry') {
            classString += ` ${styles.inactiveEntry}`;
        }
    }

    if (letterModule.type === 'given') {
        classString += ` ${styles.given}`;
    }

    // COOL LOAD ANIMATION
    classString += ` ${animation.fadeIn} ${animation.extraFast}`
    const MAX_RANDOM = .1;
    const INDEX_FACTOR = .025;
    const POSITION_FACTOR = .01;
    const delay = Math.random() * MAX_RANDOM + (INDEX_FACTOR * wordIndex) + (POSITION_FACTOR * letterPosition);
    const delayString: string = `${delay}s`

    // makes the cursor a pointer on completed pyrmaid puzzle
    // subtly hints you can click to see original word hint
    let cursorIsPointer: boolean = false;
    if (
        activeGame.state.completed
        && activeGame.state.name === 'pyramid'
    ) {
        cursorIsPointer = true;
    }

    // activates on focus
    function registerOriginalWordHint() {
        if (!activeGame || !activeGame.state.completed || !ui)
            return;

        // if a hint exists, and it matches the letterbox we clicked on, clear
        if (
            ui.originalWordHint
            && ui.originalWordHint.wordIndex === wordIndex
            && ui.originalWordHint.letterPosition === letterPosition
        ) {
            ui.hideOriginalWordHint();
            // defocus for good measure
            ui.defocusAll();
            return;
        }

        // otherwise set the hint to this letter box
        ui.showOriginalWordHint(wordIndex, letterPosition);
    }

    // activates on blur
    function clearOriginalWordHint() {
        if (!ui || !ui.originalWordHint)
            return;

        // only proceed if we match the displayed hint
        // return if mismatch
        if (
            ui.originalWordHint.wordIndex !== wordIndex
            || ui.originalWordHint.letterPosition !== letterPosition
        ) {
            return;
        }

        ui.hideOriginalWordHint();
    }

    function focusPhaseChange() {
        if (!(activeGame?.state instanceof StoryGameState)) {
            return;
        }
        const thisPhase = activeGame.state.getPhaseFromWordIndex(wordIndex);
        if (!thisPhase) {
            return;
        }
        // only process if this letterbox's phase differs from the already current phase
        if (thisPhase.index === activeGame.state.currentPhase.index) {
            return;
        }
        activeGame.update(state => {
            if (!(state instanceof StoryGameState))
                return;
            state.setPhaseByWordIndex(wordIndex);
        });
    }

    // touch letterboxes are divs, not text inputs
    // prevents on screen keyboard
    if (ui.isTouch) {
        return (
            <div
                className={classString}
                tabIndex={tabIndex}
                style={{animationDelay: delayString}}

                data-word-index={wordIndex}
                data-letter-position={letterPosition}

                ref={(el) => ui.setLetterRef(wordIndex, letterPosition, el)}
                onClick={e => {
                    e.stopPropagation();
                    ui.focus({wordIndex, letterPosition});
                    registerOriginalWordHint();
                }}

                onBlur={clearOriginalWordHint}

                onFocus={focusPhaseChange}
            >
                {letterModule.character.toUpperCase()}
            </div>
        )
    }

    return (
        <div>
            <input
                tabIndex={tabIndex}
                className={classString}
                style={cursorIsPointer 
                    ? {cursor: 'pointer', animationDelay: delayString} 
                    : {animationDelay: delayString}
                }
                
                type="text"
                maxLength={1}
                size={1}
                value={letterModule.character.toUpperCase()}
                
                data-word-index={wordIndex}
                data-letter-position={letterPosition}

                ref={(el) => ui.setLetterRef(wordIndex, letterPosition, el)}

                onClick={e => {e.stopPropagation()
                    registerOriginalWordHint();
                }}

                onBlur={clearOriginalWordHint}

                onChange={(e) => handleLetterInput({
                    wordIndex,
                    letterPosition,
                    event: e,
                    game: activeGame,
                    ui
                })}

                onKeyDown={(e) => handleKeyDown({
                    wordIndex,
                    letterPosition,
                    event: e,
                    game: activeGame,
                    ui
                })}

                onFocus={(e) => {
                    e.target.select();
                    focusPhaseChange();
                }}
            />
        </div>
    )
}